import React, { useState, useEffect } from 'react';
import { Search, Download, Users, DollarSign } from 'lucide-react';
import { consultantsAPI } from '@/api/api';
import { formatDate } from '../../utils/dateTimeUtils';

const StudentPayoutReport = () => {
  const [consultants, setConsultants] = useState([]);
  const [selectedConsultant, setSelectedConsultant] = useState('');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchConsultants();
  }, []);

  const fetchConsultants = async () => {
    try {
      const response = await consultantsAPI.getConsultants();
      setConsultants(response.data);
    } catch (error) {
      console.error('Error fetching consultants:', error);
    }
  };

  const handleConsultantChange = async (consultantId) => {
    setSelectedConsultant(consultantId);
    if (!consultantId) {
      setReportData(null);
      return;
    }

    setLoading(true);
    try {
      const response = await consultantsAPI.getStudentWisePayoutReport(consultantId);
      setReportData(response.data);
    } catch (error) {
      console.error('Error fetching report:', error);
      alert('Failed to fetch report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filteredStudents = reportData?.students?.filter(student =>
    student.student_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.student_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.student_mobile.includes(searchTerm)
  ) || [];

  const exportToCSV = () => {
    if (!reportData || !reportData.students) return;

    const headers = ['Student Number', 'Student Name', 'Admission Date', 'Course', 'Session', 'Mobile', 'Total Course Fee', 'Paid by student', 'Payout', 'Paid Payout', 'Status'];
    const csvData = filteredStudents.map(student => [
      student.student_number,
      student.student_name,
      student.admission_date,
      student.course_name,
      student.session_name,
      student.student_mobile,
      student.total_course_fee,
      student.paid_amount,
      student.payout_amount,
      student.paid_payout,
      student.payout_status
    ]);
    
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `student_payout_report_${reportData.consultant_name}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Student Wise Payout Report</h1>
          <p className="text-sm text-gray-500 mt-1">View payout details for each student by consultant</p>
        </div>
        {reportData && (
          <button
            onClick={exportToCSV}
            className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
          >
            <Download size={18} />
            Export CSV
          </button>
        )}
      </div>

      {/* Consultant Selection */}
      <div className="bg-white rounded-lg shadow p-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select Consultant
        </label>
        <select
          value={selectedConsultant}
          onChange={(e) => handleConsultantChange(e.target.value)}
          className="w-full md:w-96 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">-- Select Consultant --</option>
          {consultants.map((consultant) => (
            <option key={consultant.id} value={consultant.id}>
              {consultant.name} ({consultant.email})
            </option>
          ))}
        </select>
      </div>

      {/* Summary Cards */}
      {reportData && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Consultant</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{reportData.consultant_name}</p>
              </div>
              <Users className="text-blue-600" size={32} />
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Students</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{reportData.total_students}</p>
              </div>
              <Users className="text-purple-600" size={32} />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Payout</p>
                <p className="text-2xl font-bold text-green-600 mt-1">Rs. {(reportData.total_payout || 0).toLocaleString('en-IN')}</p>
              </div>
              <DollarSign className="text-green-600" size={32} />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Paid Payout</p>
                <p className="text-2xl font-bold text-blue-600 mt-1">Rs. {(reportData.total_paid_payout || 0).toLocaleString('en-IN')}</p>
              </div>
              <DollarSign className="text-blue-600" size={32} />
            </div>
          </div>
        </div>
      )}

      {/* Search */}
      {reportData && (
        <div className="bg-white rounded-lg shadow p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search by student name, number, or mobile..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading report...</p>
        </div>
      ) : reportData ? (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student Number</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Admission Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Course</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Session</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mobile</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Course Fee</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Paid by student</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payout</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Paid Payout</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredStudents.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="px-6 py-8 text-center text-gray-500">
                      {searchTerm ? 'No students found matching your search.' : 'No students found for this consultant.'}
                    </td>
                  </tr>
                ) : (
                  filteredStudents.map((student) => (
                    <tr key={student.student_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{student.student_number}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{student.student_name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(student.admission_date)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{student.course_name}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{student.session_name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{student.student_mobile}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Rs. {(student.total_course_fee || 0).toLocaleString('en-IN')}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">Rs. {(student.paid_amount || 0).toLocaleString('en-IN')}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">Rs. {(student.payout_amount || 0).toLocaleString('en-IN')}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-orange-600">Rs. {(student.paid_payout || 0).toLocaleString('en-IN')}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          student.payout_status === 'Paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {student.payout_status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
          <Users size={48} className="mx-auto text-gray-300 mb-4" />
          <p>Select a consultant to view student-wise payout report</p>
        </div>
      )}
    </div>
  );
};

export default StudentPayoutReport;
